import { useState } from 'react';
import { format } from 'date-fns';
import {
  Box,
  IconButton,
  CardContent,
  Grid,
  Divider,
  Typography,
  Button,
  FormHelperText
} from '@mui/material';

import { ROLES, useAuthState } from 'src/contexts/authContext';

import PencilAlt from 'src/icons/PencilAlt';
import UpdateUserEmail from './UserUpdateEmail';

import { useMutation } from '@apollo/client';
import { UNLOCK_ACCOUNT } from 'src/graphql/queries';
import AddUserRole from './AddUserRole';

const UserDetailsProfile = ({ user, executeGetUser }) => {
  const authState = useAuthState();
  const [showEmailEdit, setShowEmailEdit] = useState<boolean>(false);
  const [showRoleEdit, setShowRoleEdit] = useState<boolean>(false);

  const [
    executeUnlockAccount,
    {
      loading: unlockAccountLoading,
      data: unlockAccountData,
      error: unlockAccountError
    }
  ] = useMutation(UNLOCK_ACCOUNT);

  const onClickUnlockAccount = () => {
    executeUnlockAccount({
      variables: {
        email: user.email
      }
    });
  };

  return (
    <>
      <Divider />
      <CardContent
        sx={{
          alignItems: 'center',
          flex: '1 0 auto'
        }}
      >
        <Grid container spacing={3}>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Email
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {user.email}
              {authState.minAuthLevelMet(ROLES.CSA_MANAGER) && (
                <IconButton
                  color="primary"
                  onClick={() => {
                    setShowEmailEdit(true);
                  }}
                >
                  <PencilAlt fontSize="small" />
                </IconButton>
              )}
            </Typography>

            {showEmailEdit && (
              <UpdateUserEmail
                email={user.email}
                onDone={(newEmail) => {
                  setShowEmailEdit(false);
                  executeGetUser({
                    variables: {
                      email: newEmail,
                      fetchPolicy: 'no-cache'
                    }
                  });
                }}
              />
            )}
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              First Name
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {user.firstName}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Last Name
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {user.lastName}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Nickname
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {user.nickname}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              ID
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {user.id}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Date of Birth
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {(() => {
                if (!user.dateOfBirth) {
                  return 'Not set';
                }
                const dateOfBirth = user.dateOfBirth;
                return `${dateOfBirth.toLocaleString('default', {
                  timeZone: 'UTC',
                  month: 'long'
                })} ${dateOfBirth.getUTCDate()}, ${dateOfBirth.getUTCFullYear()}`;
              })()}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Level
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {user.currentLevel}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Experience Points
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {user.expPoints}
            </Typography>
          </Grid>
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Roles
              <IconButton
                color="primary"
                onClick={() => {
                  setShowRoleEdit(!showRoleEdit);
                }}
              >
                <PencilAlt fontSize="small" />
              </IconButton>
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {user.role.map((r) => r.id).join(', ')}
            </Typography>
          </Grid>

          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Owned Headset MAC Address
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {user.headset.length > 0
                ? user.headset[0].mac
                : 'No headset registered'}
            </Typography>
            {user.headset.length > 0 && (
              <Typography color="textSecondary" variant="body2">
                {user.headset[0].trialActivated
                  ? 'Trial activated'
                  : 'Trial not activated'}
              </Typography>
            )}
          </Grid>

          {user.lastUsedMacAddress && (
            <Grid item md={3} xs={12}>
              <Typography color="textPrimary" variant="subtitle2">
                Last Used Headset MAC Address
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {user.lastUsedMacAddress}
              </Typography>
            </Grid>
          )}
          <Grid item md={3} xs={12}>
            <Typography color="textPrimary" variant="subtitle2">
              Account Locked Until
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {!user.isAccountLocked()
                ? 'Account is not locked'
                : format(user.unlockAccountAt, 'MM/dd/yyyy HH:mm:ss')}
            </Typography>
            {user.isAccountLocked() && (
              <Button
                color="primary"
                variant="contained"
                onClick={onClickUnlockAccount}
                disabled={unlockAccountLoading || !user.isAccountLocked()}
              >
                Unlock
              </Button>
            )}
          </Grid>
        </Grid>
        {showRoleEdit && <AddUserRole email={user.email} />}
        {unlockAccountError && (
          <Box sx={{ mt: 3 }}>
            <FormHelperText error>{unlockAccountError.message}</FormHelperText>
          </Box>
        )}
        <Grid sx={{ mb: 2 }} item md={3} xs={12} justifyContent="center">
          <Box
            sx={{
              display: 'flex'
            }}
          ></Box>
        </Grid>
      </CardContent>
    </>
  );
};

export default UserDetailsProfile;
